/**
 * Channel senders (admin doc 26 §3.3 item 4, arch-11 §11): which adapter carries each channel,
 * in the order they are tried, and the switch on each.
 *
 * The adapters are code; this only ranks them and switches one off. `log` is the floor and has
 * no switch — a channel whose every sender is off or unconfigured records what it would have
 * sent and delivers nothing. Growth reads this; changing it is a superadmin act with a reason,
 * because switching off the live sender moves a whole channel at once.
 */
import { useState } from 'react';
import { Button, StatusChip } from '../components/primitives';
import { DataTable } from '../components/data';
import { Input, Toggle } from '../components/forms';
import { ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, Section } from '../components/kit';
import { errorMessage } from '../lib/api/client';
import { useConsole } from '../app/console';
import {
  useChangeGatewayRoute,
  useGatewayRoutes,
  type GatewayRoute,
} from '../lib/api/hooks/notifications';

/** One word for the row's state, the way an operator would say it on a call. */
export function routeState(route: GatewayRoute): { label: string; tone: 'success' | 'warn' | 'neutral' } {
  if (route.active) return { label: route.name === 'log' ? 'live — nothing delivered' : 'live', tone: route.name === 'log' ? 'warn' : 'success' };
  if (route.name === 'log') return { label: 'fallback', tone: 'neutral' };
  if (!route.enabled) return { label: 'switched off', tone: 'warn' };
  if (!route.configured) return { label: 'no credentials', tone: 'neutral' };
  return { label: 'standby', tone: 'neutral' };
}

export function GatewayRoutes() {
  const { toast } = useConsole();
  const routes = useGatewayRoutes();
  const change = useChangeGatewayRoute();
  const [editing, setEditing] = useState<GatewayRoute | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [priority, setPriority] = useState('100');

  const items = routes.data?.items ?? [];
  const rank = Number(priority);
  const rankOk = Number.isInteger(rank) && rank >= 1 && rank <= 999;

  return (
    <Section
      title="Channel senders"
      note="The first sender that is on and has credentials carries the channel; log is the fallback"
      pad={false}
    >
      {routes.isPending && <LoadingState message="Reading the senders…" />}
      {routes.isError && (
        <ErrorState title="Could not read the senders" cause={errorMessage(routes.error)} />
      )}
      {items.length > 0 && (
        <DataTable
          density="compact"
          columns={[
            { key: 'channel', label: 'Channel' },
            { key: 'name', label: 'Sender', mono: true },
            { key: 'state', label: 'State' },
            { key: 'priority', label: 'Order' },
            { key: 'act', label: '' },
          ]}
          rows={items.map((route) => {
            const state = routeState(route);
            return {
              id: `${route.channel}/${route.name}`,
              channel: route.channel,
              name: route.name,
              state: <StatusChip tone={state.tone} label={state.label} />,
              priority: route.name === 'log' ? 'last' : route.priority,
              act:
                route.name === 'log' ? null : (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setEnabled(route.enabled);
                      setPriority(String(route.priority));
                      setEditing(route);
                    }}
                  >
                    Change
                  </Button>
                ),
            };
          })}
        />
      )}

      {editing !== null && (
        <ConfirmAction
          open
          title={`Change ${editing.name} on ${editing.channel}`}
          phrase="CHANGE"
          tone="error"
          reason="Why this sender changes now"
          confirmLabel="Change it"
          blocked={rankOk ? undefined : ['a whole-number order from 1 to 999.']}
          consequences={[
            'Every new message on this channel goes to the first sender that is on and has credentials.',
            'If none is left, the channel falls back to log: messages are recorded and nothing is delivered.',
            'Superadmin only. The audit log keeps the old and new setting with your reason.',
          ]}
          onCancel={() => setEditing(null)}
          onConfirm={(reason) => {
            change.mutate(
              { channel: editing.channel, name: editing.name, enabled, priority: rank, reason },
              {
                onSuccess: () => {
                  setEditing(null);
                  toast({ message: 'Changed. The next message follows the new order.' });
                },
                onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
              },
            );
          }}
        >
          <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
            <label style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center' }}>
              <Toggle on={enabled} onChange={setEnabled} label="Sender on" />
              {enabled ? 'On' : 'Off'}
            </label>
            <Input
              label="Order"
              value={priority}
              onChange={setPriority}
              inputMode="numeric"
              helper="Lower goes first. A sender nobody has ranked is 100."
            />
          </div>
        </ConfirmAction>
      )}
    </Section>
  );
}
